/**
 * LocalImage Component
 * Loads images via the API server, handling auth headers for protected routes.
 *
 * Performance: For non-/api/ paths (the common case for S3-backed photos),
 * the serve URL is resolved synchronously so the browser can start loading
 * immediately without an extra React render cycle.
 */

import { useState, useEffect, useRef, memo } from 'react';
import { api } from '../../services/api';
import { getAuthHeaders } from '../../services/api/client';

interface LocalImageProps {
  src: string;
  alt: string;
  className?: string;
  style?: React.CSSProperties;
  onLoad?: () => void;
  onError?: () => void;
}

/**
 * Resolve a photo path to a URL the browser can fetch directly.
 * Exported so other components can use it for preloading.
 */
export function resolvePhotoUrl(src: string): string {
  if (!src) return '';
  if (src.startsWith('/api/')) return src;
  return api.photo.serveUrl(src);
}

/**
 * Preload an image into the browser cache. Returns a cleanup function.
 */
export function preloadImage(src: string): () => void {
  if (!src) return () => {};
  const url = resolvePhotoUrl(src);
  const img = new Image();
  img.src = url;
  // Don't abort on cleanup — let the browser cache the image
  // so it's available instantly when navigated to
  return () => {};
}

export const LocalImage = memo(function LocalImage({ src, alt, className, style, onLoad, onError }: LocalImageProps) {
  // For non-/api/ paths, resolve synchronously to skip the loading spinner
  const isApiPath = src?.startsWith('/api/');
  const syncUrl = !isApiPath && src ? resolvePhotoUrl(src) : null;

  const [imageUrl, setImageUrl] = useState<string | null>(syncUrl);
  const [loading, setLoading] = useState(isApiPath);
  const [error, setError] = useState(false);

  // Use refs for callbacks to avoid re-running effect when they change
  const onLoadRef = useRef(onLoad);
  const onErrorRef = useRef(onError);
  onLoadRef.current = onLoad;
  onErrorRef.current = onError;

  // Only run async effect for /api/ paths that need auth headers
  useEffect(() => {
    if (!isApiPath) {
      // Sync path — update URL immediately if src changed
      const url = src ? resolvePhotoUrl(src) : null;
      setImageUrl(url);
      setLoading(false);
      setError(!src);
      return;
    }

    let mounted = true;
    let blobUrl: string | null = null;

    const loadImage = async () => {
      if (!src) {
        setError(true);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(false);

        // Fetch with auth headers, create a blob URL
        const authHeaders = await getAuthHeaders();
        const res = await fetch(src, { headers: authHeaders });
        if (!res.ok) throw new Error(`Failed to load image: ${res.status}`);
        const blob = await res.blob();
        if (!mounted) return;
        blobUrl = URL.createObjectURL(blob);
        setImageUrl(blobUrl);
        setLoading(false);
      } catch (err) {
        if (!mounted) return;
        console.error('[LocalImage] Error loading image:', err);
        setError(true);
        setLoading(false);
        onErrorRef.current?.();
      }
    };

    loadImage();

    return () => {
      mounted = false;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [src, isApiPath]);

  if (loading) {
    return (
      <div className={className} style={{ ...style, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{
          width: '20px',
          height: '20px',
          border: '2px solid rgba(255,255,255,0.1)',
          borderTopColor: 'var(--accent-primary)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
        }} />
      </div>
    );
  }

  if (error || !imageUrl) {
    return (
      <div className={className} style={{ ...style, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-tertiary)' }}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ color: 'var(--text-tertiary)' }}>
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <path d="M21 15l-5-5L5 21" />
        </svg>
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={alt}
      className={className}
      style={style}
      onLoad={() => onLoadRef.current?.()}
      onError={() => {
        setError(true);
        onErrorRef.current?.();
      }}
    />
  );
});
